import { loginSchema } from '@alicerce/contracts';
import { Alert, Button, PasswordField, TextField } from '@alicerce/ui';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useSession } from '@/lib/session';
import { AuthLayout } from './layout';

/** Só aceita voltar para caminhos do próprio painel. */
export function safeNext(next: string | null) {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function LoginPage() {
  const { login } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [needsTotp, setNeedsTotp] = useState(false);
  const form = useZodForm(loginSchema, { email: '', password: '', totp: undefined as string | undefined });

  const onSubmit = form.submit(async (data) => {
    try {
      await login(data);
      navigate(safeNext(params.get('next')), { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
        form.setFormError(null);
        return;
      }
      throw e;
    }
  });

  return (
    <AuthLayout
      title="Entrar"
      subtitle="Acesse o painel do seu negócio."
      backToSite
      footer={
        <>
          Ainda não tem conta?{' '}
          <Link className="font-medium text-brand-700 hover:underline" to={`/criar-conta${params.get('next') ? `?next=${encodeURIComponent(params.get('next')!)}` : ''}`}>
            Criar conta
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {form.formError && <Alert tone="danger">{form.formError}</Alert>}
        <TextField label="E-mail" type="email" autoComplete="email" value={form.values.email} error={form.errors.email} onChange={(e) => form.set('email', e.target.value)} />
        <PasswordField
          label="Senha"
          autoComplete="current-password"
          value={form.values.password}
          error={form.errors.password}
          onChange={(e) => form.set('password', e.target.value)}
        />
        {needsTotp && (
          <TextField
            label="Código do autenticador"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            hint="Abra o aplicativo autenticador e digite os 6 números."
            value={form.values.totp ?? ''}
            error={form.errors.totp}
            onChange={(e) => form.set('totp', e.target.value || undefined)}
          />
        )}
        <Button type="submit" className="w-full" loading={form.submitting}>
          Entrar
        </Button>
        <p className="text-center text-sm">
          <Link className="text-brand-700 hover:underline" to="/esqueci-a-senha">
            Esqueci minha senha
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
